import { Fragment, useState, useEffect, useRef, useReducer } from 'react';
import { useAsync } from 'react-async';
import Layout from '../../../components/layout';
import { Notification } from '../../../components/notifications';
import NotificationModel from '../../../api/models/notification';
import { getNotificationById, getNotificationGroupTypes, updateNotification } from '../../../api/services/notificationService';
import { FieldInput } from '../../../components/fieldInput';
import SubmitButton from '../../../components/submitButton';
import NotificationRequest from '../../../api/requests/notificationRequest';
import { useHistory, useParams } from 'react-router-dom';
import NotificationGroup from '../../../api/models/notificationGroup';
import NotificationGroupType from '../../../api/models/notificationGroupType';
import { getDisciplines } from '../../../api/services/disciplineService';
import { getDistrictOptions } from '../../../api/services/districtService';
import { getDivisionOptions } from '../../../api/services/divisionService';
import { generateSelectOptions } from '../../../common/utils';
import { selectOption } from '../../../types/selectOption';
import { getRoles } from '../../../api/services/roleService';
import SelectMulti, { MultiValue } from 'react-select';
import { NotificationGroupTypeIdLookup, NotificationGroupTypes } from '../../../common/constants';
import { getSchoolsByDistricts } from '../../../api/services/schoolService';
import DOMPurify from 'dompurify';

//Import and configure the editor
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
const editorAcceptedFormats = ['header', 'font', 'size', 'bold', 'italic', 'underline', 'strike', 'blockquote', 'list', 'bullet', 'indent', 'link', 'color'];
const editorAcceptedModules = { toolbar: [['bold', 'italic', 'underline', 'strike'], [{ 'header': 1 }, { 'header': 2 }], [{ 'list': 'ordered' }, { 'list': 'bullet' }], [{ 'header': [1, 2, 3, 4, 5, 6, false] }], [{ 'color': [] }], ['clean']] };
const sanitizeInput = (input: string) => {
  return DOMPurify.sanitize(input);  
};

type Params = { id: string; }; //GET params

const fetchNotificationById = async ({ id }: any) => await getNotificationById(id);

const EditNotificationPage = () => {
  const { id } = useParams<Params>();
  const forceUpdate = useReducer(() => ({}), {})[1] as () => void
  const history = useHistory();
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [isVisible, setIsVisible] = useState<boolean>(true);
  const [message, setMessage] = useState('');
  const [groupTypes, setGroupTypes] = useState<NotificationGroupType[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<NotificationGroup[]>([]);

  //Options
  const [districtOptions, setDistrictOptions] = useState<selectOption[]>([]);
  const [divisionOptions, setDivisionOptions] = useState<selectOption[]>([]);
  const [disciplineOptions, setDisciplineOptions] = useState<selectOption[]>([]);
  const [roleOptions, setRoleOptions] = useState<selectOption[]>([]);
  const [schoolOptions, setSchoolOptions] = useState<selectOption[]>([]);

  //Selected options
  const [selectedDistrictOptions, setSelectedDistrictOptions] = useState<selectOption[]>([]);
  const [selectedDivisionOptions, setSelectedDivisionOptions] = useState<selectOption[]>([]);
  const [selectedDisciplineOptions, setSelectedDisciplineOptions] = useState<selectOption[]>([]);
  const [selectedRoleOptions, setSelectedRoleOptions] = useState<selectOption[]>([]);
  const [selectedSchoolOptions, setSelectedSchoolOptions] = useState<selectOption[]>([]);

  const districtsRef = useRef(null);

  //Selected
  const [selectedDistricts, setSelectedDistricts] = useState<number[]>([]);
  const [selectedDivisions, setSelectedDivisions] = useState<number[]>([]);
  const [selectedDisciplines, setSelectedDisciplines] = useState<number[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<number[]>([]);
  const [selectedSchools, setSelectedSchools] = useState<number[]>([]);

  //Selected Groups
  const [isDistrictGroupSelected, setIsDistrictGroupSelected] = useState(false);
  const [isDivisionGroupSelected, setIsDivisionGroupSelected] = useState(false);
  const [isDisciplineGroupSelected, setIsDisciplineGroupSelected] = useState(false);
  const [isRoleGroupSelected, setIsRoleGroupSelected] = useState(false);
  const [isSchoolGroupSelected, setIsSchoolGroupSelected] = useState(false);

  const [saveError, setSaveError] = useState('');

  //Data Fetching
  const districtsData = useAsync({
    promiseFn: getDistrictOptions,
    onResolve: (u) => setDistrictOptions(generateSelectOptions(u.data, 'value', 'label')),
  });

  const divisionsData = useAsync({
    promiseFn: getDivisionOptions,
    onResolve: (u) => setDivisionOptions(generateSelectOptions(u.data, 'value', 'label')),
  });

  const disciplinesData = useAsync({
    promiseFn: getDisciplines,
    onResolve: (u) => setDisciplineOptions(generateSelectOptions(u.data.disciplines, 'disciplineId', 'name', true)),
  });

  const rolesData = useAsync({
    promiseFn: getRoles,
    onResolve: (u) => setRoleOptions(generateSelectOptions(u.data.roles, 'roleId', 'name')),
  });

  const groupTypesData = useAsync({
    promiseFn: getNotificationGroupTypes, onResolve: (r) => {
      setGroupTypes(r.data);
    }
  });

  const { error, isPending } = useAsync({
    promiseFn: fetchNotificationById, id,
    onResolve: (r) => {
      //If no notification was returned, then hide the widget and return immediately
      if (!r || !r.data || r.data.toString() === '') {
        setIsVisible(false);
        return;
      }

      const notification: NotificationModel = r.data;
      setTitle(sanitizeInput(notification.title));
      setText(sanitizeInput(notification.text));
      setIsVisible(notification.isVisible ?? true);

      const districts: number[] = [];
      const divisions: number[] = [];
      const disciplines: number[] = [];
      const roles: number[] = [];
      const schools: number[] = [];

      notification.groups?.forEach((group: NotificationGroup) => {
        switch (group.notificationGroupTypeId) {
          case NotificationGroupTypeIdLookup.District:
            districts.push(group.actionId!);
            break;
          case NotificationGroupTypeIdLookup.Division:
            divisions.push(group.actionId!);
            break;
          case NotificationGroupTypeIdLookup.Discipline:
            disciplines.push(group.actionId!);
            break;
          case NotificationGroupTypeIdLookup.Role:
            roles.push(group.actionId!);
            break;
          case NotificationGroupTypeIdLookup.School:
            schools.push(group.actionId!);
            break;
        }
      });

      setSelectedDistricts(districts);
      setSelectedDivisions(divisions);
      setSelectedDisciplines(disciplines);
      setSelectedRoles(roles);
      setSelectedSchools(schools);

      const groups: NotificationGroup[] = [];
      districts.forEach(actionId => groups.push({ notificationGroupTypeId: NotificationGroupTypeIdLookup.District, actionId, isVisible: true }));
      divisions.forEach(actionId => groups.push({ notificationGroupTypeId: NotificationGroupTypeIdLookup.Division, actionId, isVisible: true }));
      disciplines.forEach(actionId => groups.push({ notificationGroupTypeId: NotificationGroupTypeIdLookup.Discipline, actionId, isVisible: true }));
      roles.forEach(actionId => groups.push({ notificationGroupTypeId: NotificationGroupTypeIdLookup.Role, actionId, isVisible: true }));
      schools.forEach(actionId => groups.push({ notificationGroupTypeId: NotificationGroupTypeIdLookup.School, actionId, isVisible: true }));
      setSelectedGroups(groups);
    },
  });

  /**
   * Updates the selected options
   */
  useEffect(() => {
    if (selectedDistricts && selectedDistricts.length > 0 && districtOptions && districtOptions.length > 0) {
      const options: selectOption[] = [];
      selectedDistricts.forEach(actionId => options.push({ value: actionId, label: districtOptions.find(o => o.value === actionId)?.label ?? '' }));
      setSelectedDistrictOptions(options);
    }
  }, [selectedDistricts, districtOptions]);

  useEffect(() => {
    if (selectedDivisions && selectedDivisions.length > 0 && divisionOptions && divisionOptions.length > 0) {
      const options: selectOption[] = [];
      selectedDivisions.forEach(actionId => options.push({ value: actionId, label: divisionOptions.find(o => o.value === actionId)?.label ?? '' }));
      setSelectedDivisionOptions(options);
    }
  }, [selectedDivisions, divisionOptions]);

  useEffect(() => {
    if (selectedDisciplines && selectedDisciplines.length > 0 && disciplineOptions && disciplineOptions.length > 0) {
      const options: selectOption[] = [];
      selectedDisciplines.forEach(actionId => options.push({ value: actionId, label: disciplineOptions.find(o => o.value === actionId)?.label ?? '' }));
      setSelectedDisciplineOptions(options);
    }
  }, [selectedDisciplines, disciplineOptions]);

  useEffect(() => {
    if (selectedRoles && selectedRoles.length > 0 && roleOptions && roleOptions.length > 0) {
      const options: selectOption[] = [];
      selectedRoles.forEach(actionId => options.push({ value: actionId, label: roleOptions.find(o => o.value === actionId)?.label ?? '' }));
      setSelectedRoleOptions(options);
    }
  }, [selectedRoles, roleOptions]);

  useEffect(() => {
    if (selectedSchools && selectedSchools.length > 0 && schoolOptions && schoolOptions.length > 0) {
      const options: selectOption[] = [];
      selectedSchools.forEach(actionId => options.push({ value: actionId, label: schoolOptions.find(o => o.value === actionId)?.label ?? '' }));
      setSelectedSchoolOptions(options);
    }
  }, [selectedSchools, schoolOptions]);

  /**
     * Whenever district changes, update the list of schools
     */
  useEffect(() => {
    if (selectedDistricts.length > 0) updateSchools(selectedDistricts);
    if (selectedDistricts.length === 0) setSchoolOptions([{ value: -1, label: '' }]);
  }, [selectedDistricts]);

  //Save Notification
  const saveNotification = async () => {
    const request: NotificationRequest = {
      title,
      text,
      isVisible,
      notificationId: Number(id)
    }

    //Generate the groups
    request.groups = [];
    selectedDistricts.forEach(actionId => request.groups?.push({ notificationGroupTypeId: NotificationGroupTypeIdLookup.District, actionId, isVisible }));
    selectedDivisions.forEach(actionId => request.groups?.push({ notificationGroupTypeId: NotificationGroupTypeIdLookup.Division, actionId, isVisible }));
    selectedDisciplines.forEach(actionId => request.groups?.push({ notificationGroupTypeId: NotificationGroupTypeIdLookup.Discipline, actionId, isVisible }));
    selectedRoles.forEach(actionId => request.groups?.push({ notificationGroupTypeId: NotificationGroupTypeIdLookup.Role, actionId, isVisible }));
    selectedSchools.forEach(actionId => request.groups?.push({ notificationGroupTypeId: NotificationGroupTypeIdLookup.School, actionId, isVisible }));

    return await updateNotification(request);
  }

  const updateSchools = async (districts: number[]) => {
    const response = await getSchoolsByDistricts(districts);

    //If we don't have any schools, inform the user
    if (response.data.schools.length === 0) {
      setSchoolOptions([{ value: -1, label: '' }]);
      return true;
    }

    setSchoolOptions(generateSelectOptions(response.data.schools, 'schoolId', 'name', true));
  }

  const save = useAsync({
    deferFn: saveNotification,
    onResolve: (r) => {
      history.push('/admin/notifications', {
        message: r.status >= 200 && r.status < 400 ?
          'The notification has been saved successfully' :
          'There was an error saving the notification'
      });
    }
  });

  const handleGroupTypeSelection = (groupTypeId: number) => {
    const isRemoving = selectedGroups.filter(s => s.notificationGroupTypeId === groupTypeId).length > 0;
    if (isRemoving) {
      setSelectedGroups(current => current.filter(s => s.notificationGroupTypeId !== groupTypeId));
    } else {
      setSelectedGroups(current => [...current, { notificationGroupTypeId: groupTypeId }]);
    }

    switch (groupTypeId) {
      case NotificationGroupTypeIdLookup.District:
        if (isDistrictGroupSelected) {
          setSelectedDistricts([]);
          return;
        }
        setIsDistrictGroupSelected(true);
        break;
      case NotificationGroupTypeIdLookup.Division:
        if (isDivisionGroupSelected) {
          setSelectedDivisions([]);
          return;
        }
        setIsDivisionGroupSelected(true);
        break;
      case NotificationGroupTypeIdLookup.Discipline:
        if (isDisciplineGroupSelected) {
          setSelectedDisciplines([]);
          return;
        }
        setIsDisciplineGroupSelected(true);
        break;
      case NotificationGroupTypeIdLookup.Role:
        if (isRoleGroupSelected) {
          setSelectedRoles([]);
          return;
        }
        setIsRoleGroupSelected(true);
        break;
      case NotificationGroupTypeIdLookup.School:
        if (isSchoolGroupSelected) {
          setSelectedSchools([]);
          return;
        }
        setIsSchoolGroupSelected(true);
        break;
    }
  }

  const handleGroupSelection = (options: MultiValue<selectOption>, groupTypeId: number) => {
    const ids: number[] = [];
    options.map(x => ids.push(x.value!))
    switch (groupTypeId) {
      case NotificationGroupTypeIdLookup.District:
        setSelectedDistricts(ids);
        break;
      case NotificationGroupTypeIdLookup.Division:
        setSelectedDivisions(ids);
        break;
      case NotificationGroupTypeIdLookup.Discipline:
        setSelectedDisciplines(ids);
        break;
      case NotificationGroupTypeIdLookup.Role:
        setSelectedRoles(ids);
        break;
      case NotificationGroupTypeIdLookup.School:
        setSelectedSchools(ids);
        break;
    }
  }

  //Update the checkboxes when the selections are updated
  useEffect(() => setIsDistrictGroupSelected(selectedDistricts.length > 0), [selectedDistricts]);
  useEffect(() => setIsDivisionGroupSelected(selectedDivisions.length > 0), [selectedDivisions]);
  useEffect(() => setIsDisciplineGroupSelected(selectedDisciplines.length > 0), [selectedDisciplines]);
  useEffect(() => setIsRoleGroupSelected(selectedRoles.length > 0), [selectedRoles]);
  useEffect(() => setIsSchoolGroupSelected(selectedSchools.length > 0), [selectedSchools]);

  useEffect(() => {
    setTimeout(() => {
      forceUpdate();
    }, 1000);
  }, []);

  return (
    <Layout isLoading={
      isPending ||
      groupTypesData.isPending ||
      districtsData.isPending ||
      divisionsData.isPending ||
      disciplinesData.isPending ||
      rolesData.isPending ||
      save.isPending
    }>
      <Fragment>
        {message && <Notification isSuccess>{message}</Notification>}
        {saveError && <Notification isSuccess={false}>{saveError}</Notification>}

        <div className="grid grid-cols-2">
          <div className='col-span-2 mt-4 mb-4 ml-3 text-sm'>
            <input
              type='checkbox'
              className='primary-checkbox'
              checked={isVisible}
              onChange={(e) => setIsVisible(e.currentTarget.checked)}
            />
            <label htmlFor='add-contact-is-invoice' className='px-2 primary'>Is Visible</label>
          </div>
          <div className="col-span-2 mb-4 ml-3 text-sm">
            <FieldInput
              label='Title'
              onChange={(e: any) => setTitle(sanitizeInput(e.target.value))}
              value={title}
              minLength={1}
            />
          </div>
          <div className="col-span-2 mb-4 ml-3 text-sm">
            <label>Content</label>
            <ReactQuill
              theme="snow"
              value={text}
              onChange={setText}
              formats={editorAcceptedFormats}
              modules={editorAcceptedModules}
            />
          </div>
          <div className='col-span-2 mb-2 ml-3'>
            <label>Notification Groups</label>
            <div className={`px-4 rounded-lg w-12/12 pt-2 mb-4 pb-4 bg-white shadow`}>
              {/* Districts */}
              <div className='notification-group'>
                <div className='flex items-start py-1'>
                  <div className='flex items-center h-5'>
                    <input
                      id={`notification-group-2`}
                      value={NotificationGroupTypeIdLookup.District}
                      checked={selectedGroups.filter(s => s.notificationGroupTypeId === NotificationGroupTypeIdLookup.District).length > 0}
                      onChange={(e) => handleGroupTypeSelection(Number(e.target.value))}
                      name='entry-type'
                      type='checkbox'
                      className={`bg-white`}
                    />
                  </div>
                  <div className='ml-3 text-sm'>
                    <label htmlFor={`notification-group-1`}>{NotificationGroupTypes.District}</label>
                  </div>
                </div>

                <div className="group-options">
                  <SelectMulti
                    ref={districtsRef}
                    className={`${selectedGroups.filter(s => s.notificationGroupTypeId === NotificationGroupTypeIdLookup.District).length > 0 ? "" : "hidden"}`}
                    isMulti={true}
                    options={districtOptions}
                    value={selectedDistrictOptions}
                    onChange={(e) => handleGroupSelection(e, NotificationGroupTypeIdLookup.District)}
                  />
                </div>
              </div>

              {/* Divisions */}
              <div className='notification-group'>
                <div className='flex items-start py-1'>
                  <div className='flex items-center h-5'>
                    <input
                      id={`notification-group-1`}
                      value={NotificationGroupTypeIdLookup.Division}
                      checked={selectedGroups.filter(s => s.notificationGroupTypeId === NotificationGroupTypeIdLookup.Division).length > 0}
                      onChange={(e) => handleGroupTypeSelection(Number(e.target.value))}
                      name='entry-type'
                      type='checkbox'
                      className={`bg-white`}
                    />
                  </div>
                  <div className='ml-3 text-sm'>
                    <label htmlFor={`notification-group-${NotificationGroupTypes.Division}`}>{NotificationGroupTypes.Division}</label>
                  </div>
                </div>

                <div className="group-options">
                  <SelectMulti
                    className={`${selectedGroups.filter(s => s.notificationGroupTypeId === NotificationGroupTypeIdLookup.Division).length > 0 ? "" : "hidden"}`}
                    isMulti={true}
                    options={divisionOptions}
                    value={selectedDivisionOptions}
                    onChange={(e) => handleGroupSelection(e, NotificationGroupTypeIdLookup.Division)}
                  />
                </div>
              </div>

              {/* Disciplines */}
              <div className='notification-group'>
                <div className='flex items-start py-1'>
                  <div className='flex items-center h-5'>
                    <input
                      id={`notification-group-${NotificationGroupTypes.Discipline}`}
                      value={NotificationGroupTypeIdLookup.Discipline}
                      checked={selectedGroups.filter(s => s.notificationGroupTypeId === NotificationGroupTypeIdLookup.Discipline).length > 0}
                      onChange={(e) => handleGroupTypeSelection(Number(e.target.value))}
                      name='entry-type'
                      type='checkbox'
                      className={`bg-white`}
                    />
                  </div>
                  <div className='ml-3 text-sm'>
                    <label htmlFor={`notification-group-${NotificationGroupTypes.Discipline}`}>{NotificationGroupTypes.Discipline}</label>
                  </div>
                </div>

                <div className="group-options">
                  <SelectMulti
                    className={`${selectedGroups.filter(s => s.notificationGroupTypeId === NotificationGroupTypeIdLookup.Discipline).length > 0 ? "" : "hidden"}`}
                    isMulti={true}
                    options={disciplineOptions}
                    value={selectedDisciplineOptions}
                    onChange={(e) => handleGroupSelection(e, NotificationGroupTypeIdLookup.Discipline)}
                  />
                </div>
              </div>

              {/* Roles */}
              <div className='notification-group'>
                <div className='flex items-start py-1'>
                  <div className='flex items-center h-5'>
                    <input
                      id={`notification-group-${NotificationGroupTypes.Role}`}
                      value={NotificationGroupTypeIdLookup.Role}
                      checked={selectedGroups.filter(s => s.notificationGroupTypeId === NotificationGroupTypeIdLookup.Role).length > 0}
                      onChange={(e) => handleGroupTypeSelection(Number(e.target.value))}
                      name='entry-type'
                      type='checkbox'
                      className={`bg-white`}
                    />
                  </div>
                  <div className='ml-3 text-sm'>
                    <label htmlFor={`notification-group-${NotificationGroupTypes.Role}`}>{NotificationGroupTypes.Role}</label>
                  </div>
                </div>

                <div className="group-options">
                  <SelectMulti
                    className={`${selectedGroups.filter(s => s.notificationGroupTypeId === NotificationGroupTypeIdLookup.Role).length > 0 ? "" : "hidden"}`}
                    isMulti={true}
                    options={roleOptions}
                    value={selectedRoleOptions}
                    onChange={(e) => handleGroupSelection(e, NotificationGroupTypeIdLookup.Role)}
                  />
                </div>
              </div>

              {/* Schools */}
              <div className='notification-group'>
                <div className='flex items-start py-1'>
                  <div className='flex items-center h-5'>
                    <input
                      id={`notification-group-${NotificationGroupTypes.School}`}
                      value={NotificationGroupTypeIdLookup.School}
                      checked={selectedGroups.filter(s => s.notificationGroupTypeId === NotificationGroupTypeIdLookup.School).length > 0}
                      onChange={(e) => handleGroupTypeSelection(Number(e.target.value))}
                      name='entry-type'
                      type='checkbox'
                      className={`bg-white`}
                    />
                  </div>
                  <div className='ml-3 text-sm'>
                    <label htmlFor={`notification-group-${NotificationGroupTypes.School}`}>{NotificationGroupTypes.School}</label>
                  </div>
                </div>

                <div className="group-options">
                  <SelectMulti
                    className={`${selectedGroups.filter(s => s.notificationGroupTypeId === NotificationGroupTypeIdLookup.School).length > 0 ? "" : "hidden"}`}
                    isMulti={true}
                    options={schoolOptions}
                    value={selectedSchoolOptions}
                    onChange={(e) => handleGroupSelection(e, NotificationGroupTypeIdLookup.School)}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className='col-span-2 mb-4 ml-3 text-sm'>
            <SubmitButton
              text='Save Notification'
              disabled={(!text || text.trim() === '')}
              onClick={() => save.run()}
              className='float-right'
            />
          </div>
        </div>
      </Fragment>
    </Layout>
  );
};

export default EditNotificationPage;
